import { Component, OnInit, Input } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { IsPeriodNavigatorService } from './is-period-navigator.service';

@Component({
  selector: 'is-period-navigator',
  templateUrl: './is-period-navigator.component.html',
  styleUrls: ['./is-period-navigator.component.scss']
})
export class IsPeriodNavigatorComponent implements OnInit {

  // Picker graphical and functional options
  @Input() min: string;
  @Input() max: string;
  @Input() showFrequency: boolean = true;
  @Input() showAggregation: boolean = false;
  @Input() choiceSet: boolean = true;
  @Input() tempDisable: boolean = false;
  @Input() layout: string = 'righty';
  @Input() justify: string = 'flex-end';
  @Input() selectAppearance: string = 'legacy';
  // Form control for date
  @Input() dateControl: UntypedFormControl = new UntypedFormControl(new Date());
  // Form control for frequency
  @Input() frequencyControl = new UntypedFormControl('daily');
  // Form control for aggregation
  @Input() aggregationControl = new UntypedFormControl('H');
  @Input() enableRealTime: boolean = false;
  @Input() aggregationTranslations: string[] = ['AGGR.realTime', 'AGGR.minutes', 'AGGR.hourly', 'AGGR.daily', 'AGGR.weekly', 'AGGR.monthly', 'AGGR.yearly'];

  // Form group for range date
  @Input() rangeControl = new UntypedFormGroup({
    start: new UntypedFormControl(),
    end: new UntypedFormControl()
  });

  // List of frequencies
  public frequenciesList: any[] = ['daily', 'weekly', 'monthly', 'yearly', 'personalized'];
  public aggregationList: any[] = ['min', 'H'];

  constructor(
    private _route: ActivatedRoute,
    private _translateService: TranslateService,
    public _datePicker: IsPeriodNavigatorService
  ) {
  }

  ngOnInit(): void {
    if (this.enableRealTime) {
      this.aggregationList = ['real_time', 'min', 'H'];
      this.aggregationControl.setValue('H');
    }
    this.initializeDateByParams();
  }

  private initializeDateByParams(): void {
    const params = Object.assign({}, this._route.snapshot.queryParams);
    if (params['time_from'] && params['time_to'] && params['freq'] !== 'personalized') {
      this.choiceSet = true;
      this.dateControl.setValue(new Date(params['time_from']));
      this.frequencyControl.setValue(params['freq']);
      if (this.showAggregation && params['aggregation']) {
        this.generateAggregationList();
        if (this.aggregationList.includes(params['aggregation'])) {
          this.aggregationControl.setValue(params['aggregation']);
        }
      }
      this._datePicker.setDate(this.dateControl.value, this.frequencyControl.value, this.aggregationControl.value);
    } else if (params['time_from'] && params['time_to'] && params['freq'] === 'personalized') {
      this.choiceSet = false;
      this.rangeControl.setValue({
        start: new Date(params['time_from']),
        end: new Date(params['time_to'])
      });
      this.dateControl.setValue(new Date(params['time_from']));
      this.frequencyControl.setValue(params['freq']);
      if (this.showAggregation && params['aggregation']) {
        this.generateAggregationList();
        if (this.aggregationList.includes(params['aggregation'])) {
          this.aggregationControl.setValue(params['aggregation']);
        }
      }
      this._datePicker.setDate(this.rangeControl.value, this.frequencyControl.value, this.aggregationControl.value);
    }
  }

  public setDate(event): void {
    this._datePicker.setRangeParsedDate(this.rangeControl.value);
  }

  // Called when an element used to change the date is clicked
  public changeDate(event): void {
    let newDate;
    if (typeof event === 'number' || typeof event === 'string') {
      if (this.frequencyControl?.value === 'daily') {
        this.choiceSet = true;
        newDate = new Date(moment(this.dateControl?.value).add(event, 'day').toISOString());
        newDate.setHours(0, 0, 0, 0);
        this.dateControl.setValue(newDate);
        this.rangeControl.reset();
        this._datePicker.setDate(this.dateControl.value, this.frequencyControl.value, this.aggregationControl.value);
      } else if (this.frequencyControl?.value === 'weekly') {
        this.choiceSet = true;
        newDate = new Date(moment(this.dateControl?.value).add(event, 'week').toISOString());
        newDate.setHours(0, 0, 0, 0);
        this.dateControl.setValue(newDate);
        this.rangeControl.reset();
        this._datePicker.setDate(this.dateControl.value, this.frequencyControl.value, this.aggregationControl.value);
      } else if (this.frequencyControl?.value === 'monthly') {
        this.choiceSet = true;
        newDate = new Date(moment(this.dateControl?.value).add(event, 'month').toISOString());
        newDate.setHours(0, 0, 0, 0);
        this.dateControl.setValue(newDate);
        this.rangeControl.reset();
        this._datePicker.setDate(this.dateControl.value, this.frequencyControl.value, this.aggregationControl.value);
      } else if (this.frequencyControl?.value === 'yearly') {
        this.choiceSet = true;
        newDate = new Date(moment(this.dateControl?.value).add(event, 'year').toISOString());
        newDate.setHours(0, 0, 0, 0);
        this.dateControl.setValue(newDate);
        this.rangeControl.reset();
        this._datePicker.setDate(this.dateControl.value, this.frequencyControl.value, this.aggregationControl.value);
      } else if (this.frequencyControl?.value === 'personalized') {
        this.choiceSet = false;
        const start = this.rangeControl?.value?.start ?
          new Date(moment(this.rangeControl?.value?.start).toISOString()) :
          new Date(moment(this.dateControl?.value).toISOString());

        start.setHours(0, 0, 0, 0);
        const end = this.rangeControl?.value?.end ?
          new Date(moment(this.rangeControl?.value?.end).toISOString()) :
          new Date(moment(this.dateControl?.value).add(1, 'day').toISOString());
        end.setHours(0, 0, 0, 0);
        this.dateControl.setValue(start);
        this.rangeControl.setValue({
          start: start,
          end: end
        });

        this._datePicker.setDate(this.rangeControl.value, this.frequencyControl.value, this.aggregationControl.value);
      }
    } else if (typeof event.value === 'object') {
      this.dateControl.setValue(new Date(event.value));
      this._datePicker.setDate(this.dateControl.value, this.frequencyControl.value, this.aggregationControl.value);
    }
  }

  public changeFrequency(event): void {
    const params = Object.assign({}, this._route.snapshot.queryParams);
    if (this.showAggregation) {
      this.generateAggregationList();
      // TO MANTAIN COMPATIBILTY WITH ACTUAL
      if (!this.enableRealTime && this.aggregationList.includes(params['aggregation'])) {
        this.aggregationControl.setValue(params['aggregation']);
      }
    }
    this.changeDate(event);
  }

  public changeAggregation(event): void {
    this.changeDate(event);
  }

  // Returns current selected date formatted depending on the frequency
  public getCurrentDate(): string {
    let c: string;
    if (this.frequencyControl.value === 'daily') {
      c = moment(this.dateControl.value).format(this._translateService.instant('FORMATS.extended_date'));
    } else if (this.frequencyControl.value === 'monthly') {
      c = moment(this.dateControl.value).format(this._translateService.instant('MMMM YYYY'));
    } else if (this.frequencyControl.value === 'weekly') {
      c = moment(this.dateControl.value).format(this._translateService.instant('FORMATS.extended_date'));
      c += ' - ' + moment(this.dateControl.value).add(1, 'week').subtract(1, 'ms').format(this._translateService.instant('FORMATS.extended_date'));
    } else if (this.frequencyControl.value === 'yearly') {
      c = moment(this.dateControl.value).format('YYYY');
    } else if (this.frequencyControl.value === 'personalized') {
      c = moment(this.rangeControl.value.start).format('DD MMM YYYY') + ' - ' + moment(this.rangeControl.value.end).format('DD MMM YYYY');
    }
    return c;
  }

  // Deactivates the buttons if max/min are set and the date range limit is reached
  public canChangeWith(param: number): boolean {
    const max = new Date(this.max);
    const min = new Date(this.min);
    const dateWhereMove = new Date(
      moment(this.dateControl?.value).add(param, 'days').toISOString()
    );
    if (this.max && max && dateWhereMove > max) {
      return false;
    }
    if (this.min && min && dateWhereMove < min) {
      return false;
    }
    return true;
  }

  public generateAggregationList(): void {
    this.aggregationList = [];
    this.aggregationControl.reset();
    switch (this.frequencyControl.value) {
      case 'daily':
        if (this.enableRealTime) {
          this.aggregationList.push('real_time');
        }
        this.aggregationList.push('min');
        this.aggregationList.push('H');
        this.aggregationControl.setValue('H');
        break;
      case 'weekly':
        if (this.enableRealTime) {
          this.aggregationList.push('real_time');
        }
        this.aggregationList.push('min');
        this.aggregationList.push('H');
        this.aggregationList.push('D');
        this.aggregationControl.setValue('D');
        break;
      case 'monthly':
        this.aggregationList.push('H');
        this.aggregationList.push('D');
        this.aggregationList.push('W');
        this.aggregationControl.setValue('D');
        break;
      case 'yearly':
        this.aggregationList.push('H');
        this.aggregationList.push('D');
        this.aggregationList.push('W');
        this.aggregationList.push('M');
        this.aggregationControl.setValue('M');
        break;
      case 'personalized':
        if (this.enableRealTime) {
          this.aggregationList.push('real_time');
        }
        this.aggregationList.push('min');
        this.aggregationList.push('H');
        this.aggregationList.push('D');
        this.aggregationList.push('W');
        this.aggregationList.push('M');
        this.aggregationControl.setValue('D');
        break;
    }
  }

  public formatAggregation(aggr: string): string {
    switch (aggr) {
      case 'real_time':
        return this.aggregationTranslations[0];
      case 'min':
        return this.aggregationTranslations[1];
      case 'H':
        return this.aggregationTranslations[2];
      case 'D':
        return this.aggregationTranslations[3];
      case 'W':
        return this.aggregationTranslations[4];
      case 'M':
        return this.aggregationTranslations[5];
      case 'Y':
        return this.aggregationTranslations[6];
      default:
        return;
    }
  }

  public calendarView(): string {
    switch (this.frequencyControl.value) {
      case 'yearly':
        return 'multi-year';
      case 'monthly':
        return 'year';
      default:
        return 'month';
    }
  }
}
